import React, { useState, useEffect } from 'react';
import { apiPost, zeigeApiFehler } from '../../lib/api';
import { toast } from '../../lib/toast';
import { LdSheet } from '../ui/LdSheet';
import { LdAuswahl } from '../ld/LdFenster';

export interface MailVorschlag {
  vorname: string | null;
  nachname: string | null;
  eltern_email: string | null;
  klasse_id: number | null;
  confidence: number;
  begruendung: string;
}

interface Props {
  open: boolean;
  vorschlag: MailVorschlag | null;
  klassen: { id: number; name: string }[];
  onClose: () => void;
  onCreated: () => void;
}

const inp: React.CSSProperties = {
  width: '100%', padding: '10px 13px', borderRadius: 10,
  border: '1.5px solid var(--ld-outline-soft)', fontSize: 15, outline: 'none',
  boxSizing: 'border-box', background: 'var(--ld-bg-input)', color: 'var(--ld-text-primary)',
};
const lbl: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, color: 'var(--ld-text-secondary)',
  letterSpacing: .4, display: 'block', marginBottom: 5,
};

export function NeuerSchuelerAusMailModal({ open, vorschlag, klassen, onClose, onCreated }: Props) {
  const [vorname, setVorname]     = useState('');
  const [nachname, setNachname]   = useState('');
  const [klasseId, setKlasseId]   = useState<number | ''>('');
  const [elternEmail, setElternEmail] = useState('');
  const [elternName, setElternName]   = useState('');
  const [geburtsdatum, setGeburtsdatum] = useState('');
  const [telefon, setTelefon]     = useState('');
  const [laden, setLaden]         = useState(false);

  useEffect(() => {
    if (open && vorschlag) {
      setVorname(vorschlag.vorname ?? '');
      setNachname(vorschlag.nachname ?? '');
      setKlasseId(vorschlag.klasse_id ?? '');
      setElternEmail(vorschlag.eltern_email ?? '');
      setElternName(''); setGeburtsdatum(''); setTelefon('');
    }
  }, [open, vorschlag]);

  if (!open || !vorschlag) return null;

  const niedrig = vorschlag.confidence < 0.5;
  const kannAnlegen = vorname.trim() && nachname.trim() && klasseId !== '' && elternEmail.trim();

  async function anlegen() {
    if (!kannAnlegen) { toast('Bitte Vorname, Nachname, Klasse und Eltern-Email ausfüllen.', 'error'); return; }
    setLaden(true);
    try {
      // 1. Schüler anlegen
      const neu = await apiPost<any>('/api/schueler', {
        vorname: vorname.trim(), nachname: nachname.trim(), klasse_id: Number(klasseId),
        geburtsdatum: geburtsdatum.trim() || undefined,
        eltern_email: elternEmail.trim() || undefined,
        eltern_telefon: telefon.trim() || undefined,
      });

      // 2. Eltern-Verknüpfung (A1)
      try {
        await apiPost(`/api/schueler/${neu.id}/eltern`, {
          name: elternName.trim() || null, email: elternEmail.trim(),
          telefon: telefon.trim() || null, rolle: 'unbekannt', ist_hauptkontakt: true,
        });
      } catch { /* Schüler ist angelegt; Eltern-Verknüpfung best-effort */ }

      toast('Schüler angelegt + mit Mail-Adresse verknüpft', 'success');
      window.dispatchEvent(new Event('lehrerdesk-schueler-reload'));
      onCreated();
      onClose();
    } catch (e) {
      zeigeApiFehler(e, 'Anlegen fehlgeschlagen');
    } finally {
      setLaden(false);
    }
  }

  return (
    <LdSheet open onClose={onClose} maxWidth={520} kopf={{ titel: 'Neuen Schüler anlegen', kontext: 'Aus der Mail' }}>
      <div>

        {/* KI-Hinweis */}
        <div style={{ background: niedrig ? '#FEF2F2' : '#EDE9F8', borderRadius: 'var(--ld-radius-md)', padding: '10px 14px', marginBottom: 16, fontSize: 12, color: niedrig ? '#991B1B' : 'var(--ld-primary)', lineHeight: 1.5 }}>
          {niedrig
            ? 'Aus der Mail ließ sich wenig sicher erkennen. Bitte Felder prüfen oder selbst eingeben.'
            : <>Aus der Mail ausgefüllt, bitte kurz prüfen ({Math.round(vorschlag.confidence * 100)}% Sicherheit){vorschlag.begruendung ? <><br />„{vorschlag.begruendung}"</> : null}</>
          }
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={lbl}>Vorname</label>
            <input value={vorname} onChange={e => setVorname(e.target.value)} style={inp} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={lbl}>Nachname</label>
            <input value={nachname} onChange={e => setNachname(e.target.value)} style={inp} />
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>Klasse</label>
          <LdAuswahl value={klasseId === '' ? '' : String(klasseId)} onChange={e => setKlasseId(e.target.value === '' ? '' : Number(e.target.value))}>
            <option value="">— Klasse wählen —</option>
            {klassen.map(k => <option key={k.id} value={String(k.id)}>{k.name}</option>)}
          </LdAuswahl>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>E-Mail der Eltern</label>
          <input value={elternEmail} onChange={e => setElternEmail(e.target.value)} type="email" style={inp} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>Name der Eltern (optional)</label>
          <input value={elternName} onChange={e => setElternName(e.target.value)} placeholder="z.B. Müller, Andrea" style={inp} />
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
          <div style={{ flex: 1 }}>
            <label style={lbl}>Geburtsdatum (optional)</label>
            <input value={geburtsdatum} onChange={e => setGeburtsdatum(e.target.value)} placeholder="TT.MM.JJJJ" style={inp} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={lbl}>Telefon (optional)</label>
            <input value={telefon} onChange={e => setTelefon(e.target.value)} style={inp} />
          </div>
        </div>

        <div style={{ fontSize: 12, color: '#92400E', background: '#FFF7ED', borderRadius: 'var(--ld-radius-md)', padding: '8px 12px', marginBottom: 16 }}>
          Bitte die Felder vor dem Anlegen prüfen.
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '12px 0', borderRadius: 12, border: '1.5px solid var(--ld-outline-soft)', background: 'var(--ld-bg-input)', fontSize: 15, fontWeight: 600, cursor: 'pointer', color: 'var(--ld-text-secondary)' }}>Abbrechen</button>
          <button onClick={anlegen} disabled={!kannAnlegen || laden} style={{ flex: 2, padding: '12px 0', borderRadius: 12, border: 'none', background: !kannAnlegen || laden ? '#D0CAEC' : 'var(--ld-primary)', color: 'white', fontSize: 15, fontWeight: 700, cursor: !kannAnlegen || laden ? 'default' : 'pointer' }}>
            {laden ? '…' : 'Schüler anlegen'}
          </button>
        </div>
      </div>
    </LdSheet>
  );
}
